import React, { useState } from 'react';
import { ChevronDownIcon, ShoppingCartIcon, ChevronLeftIcon, ChevronRightIcon, ClipboardListIcon, FileTextIcon, MapIcon, ClockIcon, BookOpenIcon, SearchIcon, FileIcon, GlobeIcon, ActivityIcon, FacebookIcon, TwitterIcon, InstagramIcon, YoutubeIcon, MessageCircleIcon, PlayIcon, ArrowLeftIcon, PrinterIcon, EditIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
// Mock Data for Cascading Table
type CascadeData = {
  [division: string]: {
    [district: string]: {
      [upazila: string]: {
        mouzas: string[];
      };
    };
  };
};
const cascadeData: CascadeData = {
  ঢাকা: {
    ঢাকা: {
      সাভার: {
        mouzas: ['আশুলিয়া', 'ধামসোনা', 'শিমুলিয়া']
      },
      ধামরাই: {
        mouzas: ['কুশুরা', 'গাংগুটিয়া', 'বালিয়া']
      }
    },
    গাজীপুর: {
      কালীগঞ্জ: {
        mouzas: ['জামালপুর', 'বক্তারপুর', 'তুমুলিয়া']
      },
      'গাজীপুর সদর': {
        mouzas: ['ভাওয়াল', 'মির্জাপুর', 'পুবাইল']
      }
    },
    নরসিংদী: {
      শিবপুর: {
        mouzas: ['পুটিয়া', 'মাছিমপুর', 'বাঘাব']
      },
      পলাশ: {
        mouzas: ['ঘোড়াশাল', 'ডাঙ্গা', 'জিনারদী']
      }
    }
  },
  চট্টগ্রাম: {
    চট্টগ্রাম: {
      পটিয়া: {
        mouzas: ['কোলাগাঁও', 'হাইডগাঁও', 'কচুয়াই']
      },
      বোয়ালখালী: {
        mouzas: ['কধুরখীল', 'শাকপুরা', 'সারোয়াতলী']
      }
    },
    কক্সবাজার: {
      উখিয়া: {
        mouzas: ['রাজাপালং', 'রত্নাপালং', 'হলদিয়াপালং']
      }
    }
  },
  রাজশাহী: {
    রাজশাহী: {
      পবা: {
        mouzas: ['হুজুরীপাড়া', 'দর্শনপাড়া', 'দামকুড়া']
      }
    }
  },
  খুলনা: {
    খুলনা: {
      ডুমুরিয়া: {
        mouzas: ['ভান্ডারপাড়া', 'মাগুরখালী', 'শোভনা']
      }
    }
  },
  বরিশাল: {
    বরিশাল: {
      বাকেরগঞ্জ: {
        mouzas: ['গারুড়িয়া', 'কলসকাঠী', 'দুদল']
      }
    }
  },
  সিলেট: {
    সিলেট: {
      গোয়াইনঘাট: {
        mouzas: ['রুস্তমপুর', 'লেংগুড়া', 'তোয়াকুল']
      }
    }
  },
  রংপুর: {
    রংপুর: {
      পীরগঞ্জ: {
        mouzas: ['চৈত্রকোল', 'ভেন্ডাবাড়ী', 'বড়দরগাহ']
      }
    }
  },
  ময়মনসিংহ: {
    ময়মনসিংহ: {
      ত্রিশাল: {
        mouzas: ['বালিহাটি', 'কানিহারী', 'রামপুর']
      }
    }
  }
};
const khatianTypes = ['বিআরএস', 'সিএস', 'এসএ', 'আরএস', 'দিয়ারা', 'পেটি'];
const mockKhatians = [{
  no: '১০২৩',
  owner: 'মো. রহিম উদ্দিন'
}, {
  no: '১০২৪',
  owner: 'আব্দুল করিম'
}, {
  no: '১০২৫',
  owner: 'ফাতেমা বেগম'
}, {
  no: '১০২৬',
  owner: 'মো. জামাল হোসেন'
}, {
  no: '১০২৭',
  owner: 'আবু তাহের আলী'
}, {
  no: '১০২৮',
  owner: 'নূর মোহাম্মদ'
}, {
  no: '১০২৯',
  owner: 'সাইফুল ইসলাম'
}, {
  no: '১০৩০',
  owner: 'রহিমা খাতুন'
}, {
  no: '১০৩১',
  owner: 'মো. হাসান আলী'
}, {
  no: '১০৩২',
  owner: 'কামাল উদ্দিন'
}];
export function HomePage({
  navigateTo
}: {
  navigateTo: (page: string) => void;
}) {
  const {
    language,
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('survey');
  // Cascading Table State
  const [selectedDivision, setSelectedDivision] = useState<string | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [selectedUpazila, setSelectedUpazila] = useState<string | null>(null);
  const [selectedKhatianType, setSelectedKhatianType] = useState<string | null>(null);
  const [selectedMouza, setSelectedMouza] = useState<string | null>(null);
  const [selectedKhatian, setSelectedKhatian] = useState<{
    no: string;
    owner: string;
  } | null>(null);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const handleDivisionClick = (div: string) => {
    setSelectedDivision(div);
    setSelectedDistrict(null);
    setSelectedUpazila(null);
    setSelectedKhatianType(null);
    setSelectedMouza(null);
  };
  const handleDistrictClick = (dist: string) => {
    setSelectedDistrict(dist);
    setSelectedUpazila(null);
    setSelectedKhatianType(null);
    setSelectedMouza(null);
  };
  const handleUpazilaClick = (upa: string) => {
    setSelectedUpazila(upa);
    setSelectedKhatianType(null);
    setSelectedMouza(null);
  };
  const handleKhatianTypeClick = (type: string) => {
    setSelectedKhatianType(type);
    setSelectedMouza(null);
  };
  const handleMouzaClick = (mouza: string) => {
    setSelectedMouza(mouza);
  };
  return <div className="min-h-screen bg-[#F5F7F9] font-sans flex flex-col" data-id="element-889">
      {/* Top Date Bar */}
      <div className="bg-[#006A4E] text-white py-1.5 px-4 text-center text-sm" data-id="element-890">
        বৃহস্পতিবার, ২৭ ফাল্গুন ১৪৩২, ১২ মার্চ ২০২৬
      </div>

      {/* Main Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50" data-id="element-891">
        <div className="max-w-[1400px] mx-auto px-4 flex justify-between items-center h-16" data-id="element-892">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => navigateTo('home')} data-id="element-893">
            <img src="https://cdn.magicpatterns.com/uploads/9zTMD1Cxv4NB3L18v8BPEZ/dlrmslogo.png" alt="DLRMS Logo" className="h-10 object-contain mr-3" data-id="element-894" />
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-6 text-[15px] font-medium text-gray-800" data-id="element-895">
            <a href="#" className="hover:text-[#006A4E]" data-id="element-896">
              হোম
            </a>
            <div className="flex items-center cursor-pointer hover:text-[#006A4E]" data-id="element-897">
              আমাদের সম্পর্কে <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-898" />
            </div>
            <div className="flex items-center cursor-pointer hover:text-[#006A4E]" data-id="element-899">
              জ্ঞাতব্য <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-900" />
            </div>
            <a href="#" className="hover:text-[#006A4E]" data-id="element-901">
              পরিপত্র
            </a>
            <a href="#" className="hover:text-[#006A4E]" data-id="element-902">
              আবেদনের অবস্থা
            </a>
            <a href="#" className="hover:text-[#006A4E]" data-id="element-903">
              নির্দেশিকা
            </a>
            <a href="#" className="hover:text-[#006A4E]" data-id="element-904">
              খতিয়ান ও ম্যাপ অনুসন্ধান
            </a>
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-4" data-id="element-905">
            <button className="w-8 h-8 bg-[#006A4E] text-white rounded-full flex items-center justify-center hover:bg-[#004D3A] transition-colors" data-id="element-906">
              <ShoppingCartIcon className="w-4 h-4" data-id="element-907" />
            </button>
            <div className="relative" data-id="element-908">
              <button onClick={() => setShowLoginDropdown(!showLoginDropdown)} className="bg-[#006A4E] text-white px-4 py-1.5 rounded-full flex items-center text-sm font-medium hover:bg-[#004D3A] transition-colors" data-id="element-909">
                লগইন{' '}
                <ChevronDownIcon className={`w-4 h-4 ml-1 transition-transform ${showLoginDropdown ? 'rotate-180' : ''}`} data-id="element-910" />
              </button>
              {showLoginDropdown && <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50" data-id="element-911">
                  <button onClick={() => {
                navigateTo('login');
                setShowLoginDropdown(false);
              }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#E8F5E9] hover:text-[#006A4E] transition-colors font-medium" data-id="element-912">
                    লগইন
                  </button>
                  <button onClick={() => {
                navigateTo('adminLogin');
                setShowLoginDropdown(false);
              }} className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#E8F5E9] hover:text-[#006A4E] transition-colors font-medium" data-id="element-913">
                    প্রশাসনিক
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-[300px] md:h-[400px] w-full overflow-hidden" data-id="element-914">
        {/* Background Image - Aerial view of green fields */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1589923188900-85dae523342b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)'
      }} data-id="element-915">
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/30" data-id="element-916"></div>
        </div>

        {/* Hero Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4" data-id="element-917">
          <button className="absolute left-4 md:left-8 w-10 h-10 flex items-center justify-center hover:bg-black/20 rounded-full transition-colors" data-id="element-918">
            <ChevronLeftIcon className="w-8 h-8" data-id="element-919" />
          </button>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center drop-shadow-lg" data-id="element-920">
            ভূমি রেকর্ড ও ম্যাপ সেবায় আপনাকে স্বাগতম
          </h1>

          <button className="absolute right-4 md:right-8 w-10 h-10 flex items-center justify-center hover:bg-black/20 rounded-full transition-colors" data-id="element-921">
            <ChevronRightIcon className="w-8 h-8" data-id="element-922" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-12 flex space-x-2" data-id="element-923">
            <div className="w-2 h-2 rounded-full bg-white" data-id="element-924"></div>
            <div className="w-2 h-2 rounded-full bg-white/50" data-id="element-925"></div>
            <div className="w-2 h-2 rounded-full bg-white/50" data-id="element-926"></div>
            <div className="w-2 h-2 rounded-full bg-white/50" data-id="element-927"></div>
            <div className="w-2 h-2 rounded-full bg-white/50" data-id="element-928"></div>
          </div>
        </div>
      </div>

      {/* Main Content Container - Overlaps Hero */}
      <main className="flex-1 max-w-[1400px] w-full mx-auto px-4 -mt-8 relative z-10 pb-16" data-id="element-929">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8" data-id="element-930">
          {/* Quick Services Tabs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-id="element-931">
            <button onClick={() => setActiveTab('survey')} className={`flex flex-col items-center justify-center py-6 px-4 rounded-xl border transition-all ${activeTab === 'survey' ? 'bg-[#006A4E] text-white border-[#006A4E] shadow-md' : 'bg-white text-[#006A4E] border-gray-200 hover:border-[#006A4E] hover:shadow-sm'}`} data-id="element-932">
              <ClipboardListIcon className="w-10 h-10 mb-3" strokeWidth={1.5} data-id="element-933" />
              <span className="font-bold text-[15px]" data-id="element-934">সার্ভে খতিয়ান</span>
            </button>

            <button onClick={() => setActiveTab('mutation')} className={`flex flex-col items-center justify-center py-6 px-4 rounded-xl border transition-all ${activeTab === 'mutation' ? 'bg-[#006A4E] text-white border-[#006A4E] shadow-md' : 'bg-white text-[#006A4E] border-gray-200 hover:border-[#006A4E] hover:shadow-sm'}`} data-id="element-935">
              <FileTextIcon className="w-10 h-10 mb-3" strokeWidth={1.5} data-id="element-936" />
              <span className="font-bold text-[15px]" data-id="element-937">নামজারি খতিয়ান</span>
            </button>

            <button onClick={() => {
            setActiveTab('map');
            navigateTo('mouzaMap');
          }} className={`flex flex-col items-center justify-center py-6 px-4 rounded-xl border transition-all ${activeTab === 'map' ? 'bg-[#006A4E] text-white border-[#006A4E] shadow-md' : 'bg-white text-[#006A4E] border-gray-200 hover:border-[#006A4E] hover:shadow-sm'}`} data-id="element-938">
              <MapIcon className="w-10 h-10 mb-3" strokeWidth={1.5} data-id="element-939" />
              <span className="font-bold text-[15px]" data-id="element-940">মৌজা ম্যাপ</span>
            </button>

            <button onClick={() => setActiveTab('status')} className={`flex flex-col items-center justify-center py-6 px-4 rounded-xl border transition-all ${activeTab === 'status' ? 'bg-[#006A4E] text-white border-[#006A4E] shadow-md' : 'bg-white text-[#006A4E] border-gray-200 hover:border-[#006A4E] hover:shadow-sm'}`} data-id="element-941">
              <GlobeIcon className="w-10 h-10 mb-3" strokeWidth={1.5} data-id="element-942" />
              <span className="font-bold text-[15px]" data-id="element-943">আবেদনের অবস্থা</span>
            </button>

            <button onClick={() => setActiveTab('guide')} className={`flex flex-col items-center justify-center py-6 px-4 rounded-xl border transition-all ${activeTab === 'guide' ? 'bg-[#006A4E] text-white border-[#006A4E] shadow-md' : 'bg-white text-[#006A4E] border-gray-200 hover:border-[#006A4E] hover:shadow-sm'}`} data-id="element-944">
              <BookOpenIcon className="w-10 h-10 mb-3" strokeWidth={1.5} data-id="element-945" />
              <span className="font-bold text-[15px]" data-id="element-946">নির্দেশিকা</span>
            </button>
          </div>

          {/* Scrolling Red Text */}
          <div className="overflow-hidden whitespace-nowrap mb-8" data-id="element-947">
            <p className="text-red-600 font-bold text-sm animate-[marquee_20s_linear_infinite] inline-block" data-id="element-948">
              এখন থেকে খতিয়ানের কিউ আর কোড সম্বলিত অনলাইন/তাৎক্ষণিক কপি পেতে
              খতিয়ান অনুসন্ধান করে আবেদন করুন।
            </p>
          </div>

          {/* Survey Khatian Information Section */}
          <div className="mb-12" data-id="element-949">
            <h2 className="text-2xl font-bold text-[#006A4E] mb-4 underline underline-offset-4" data-id="element-950">
              সার্ভে খতিয়ানের তথ্য
            </h2>

            {/* Khatian Detail View */}
            {selectedKhatian ? <div data-id="element-951">
                {/* Back button and action */}
                <div className="flex items-center gap-3 mb-6" data-id="element-952">
                  <button onClick={() => setSelectedKhatian(null)} className="w-9 h-9 bg-[#006A4E] text-white rounded-full flex items-center justify-center hover:bg-[#004D3A] transition-colors" data-id="element-953">
                    <ArrowLeftIcon className="w-4 h-4" data-id="element-954" />
                  </button>
                  <button className="bg-[#006A4E] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#004D3A] transition-colors" data-id="element-955">
                    খতিয়ানের দেখুন
                  </button>
                </div>

                {/* Paper-style Khatian Card */}
                <div className="flex justify-center" data-id="element-956">
                  <div className="relative w-full max-w-lg" data-id="element-957">
                    {/* Paper texture card */}
                    <div className="rounded-lg shadow-lg p-8 border border-amber-200" style={{
                  background: 'linear-gradient(135deg, #f5e6c8 0%, #e8d5a8 30%, #f0deb8 60%, #e5d0a0 100%)',
                  backgroundImage: `
                          linear-gradient(135deg, #f5e6c8 0%, #e8d5a8 30%, #f0deb8 60%, #e5d0a0 100%),
                          url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.08'/%3E%3C/svg%3E")
                        `
                }} data-id="element-958">
                      {/* Title */}
                      <h3 className="text-xl font-bold text-center text-gray-900 mb-6 pb-3 border-b-2 border-amber-300/50" data-id="element-959">
                        খতিয়ান নং - {selectedKhatian.no}
                      </h3>

                      {/* Details Grid */}
                      <div className="space-y-3 text-[15px]" data-id="element-960">
                        {/* Left dots decoration */}
                        <div className="flex items-start" data-id="element-961">
                          <div className="flex items-center gap-1 mr-2 mt-1.5" data-id="element-962">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-963"></div>
                          </div>
                          <div className="flex" data-id="element-964">
                            <span className="font-bold text-gray-800 w-32 shrink-0" data-id="element-965">
                              বিভাগ
                            </span>
                            <span className="text-gray-600" data-id="element-966">
                              : {selectedDivision || 'ঢাকা'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start" data-id="element-967">
                          <div className="flex items-center gap-1 mr-2 mt-1.5" data-id="element-968">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-969"></div>
                          </div>
                          <div className="flex" data-id="element-970">
                            <span className="font-bold text-gray-800 w-32 shrink-0" data-id="element-971">
                              জেলা
                            </span>
                            <span className="text-gray-600" data-id="element-972">
                              : {selectedDistrict || 'ঢাকা'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start" data-id="element-973">
                          <div className="flex items-center gap-1 mr-2 mt-1.5" data-id="element-974">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-975"></div>
                          </div>
                          <div className="flex" data-id="element-976">
                            <span className="font-bold text-gray-800 w-32 shrink-0" data-id="element-977">
                              উপজেলা
                            </span>
                            <span className="text-gray-600" data-id="element-978">
                              : {selectedUpazila || 'মিরপুর রাজস্ব সার্কেল'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start" data-id="element-979">
                          <div className="flex items-center gap-1 mr-2 mt-1.5" data-id="element-980">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-981"></div>
                          </div>
                          <div className="flex" data-id="element-982">
                            <span className="font-bold text-gray-800 w-32 shrink-0" data-id="element-983">
                              সার্ভে
                            </span>
                            <span className="text-gray-600" data-id="element-984">
                              : {selectedKhatianType || 'আর এস'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start" data-id="element-985">
                          <div className="flex items-center gap-1 mr-2 mt-1.5" data-id="element-986">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-987"></div>
                          </div>
                          <div className="flex" data-id="element-988">
                            <span className="font-bold text-gray-800 w-32 shrink-0" data-id="element-989">
                              মৌজা
                            </span>
                            <span className="text-gray-600" data-id="element-990">
                              : {selectedMouza || '১ - গ্রাম জাঁতুলিয়া'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start" data-id="element-991">
                          <div className="flex items-center gap-1 mr-2 mt-1.5" data-id="element-992">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-993"></div>
                          </div>
                          <div className="flex" data-id="element-994">
                            <span className="font-bold text-gray-800 w-32 shrink-0" data-id="element-995">
                              জমির পরিমাণ
                            </span>
                            <span className="text-gray-600" data-id="element-996">
                              : ৩.৮৬০০ (একর)
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start" data-id="element-997">
                          <div className="flex items-center gap-1 mr-2 mt-1.5" data-id="element-998">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-999"></div>
                          </div>
                          <div className="flex" data-id="element-1000">
                            <span className="font-bold text-gray-800 w-32 shrink-0" data-id="element-1001">
                              মালিকের নাম
                            </span>
                            <span className="text-gray-600" data-id="element-1002">
                              : {selectedKhatian.owner},...
                            </span>
                          </div>
                        </div>

                        <div className="flex items-start" data-id="element-1003">
                          <div className="flex items-center gap-1 mr-2 mt-1.5" data-id="element-1004">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-500" data-id="element-1005"></div>
                          </div>
                          <div className="flex" data-id="element-1006">
                            <span className="font-bold text-gray-800 w-32 shrink-0" data-id="element-1007">
                              দাগ নং
                            </span>
                            <span className="text-gray-600" data-id="element-1008">: ২৭৩,...</span>
                          </div>
                        </div>
                      </div>

                      {/* Pagination dots */}
                      <div className="flex justify-center gap-2 mt-6 mb-4" data-id="element-1009">
                        <div className="w-2 h-2 rounded-full bg-gray-500" data-id="element-1010"></div>
                        <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-1011"></div>
                        <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-1012"></div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap justify-center gap-3 mt-4" data-id="element-1013">
                        <button className="bg-[#006A4E] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#004D3A] transition-colors flex items-center gap-2" data-id="element-1014">
                          <ShoppingCartIcon className="w-4 h-4" data-id="element-1015" />
                          ঝুড়িতে রাখুন
                        </button>
                        <button className="bg-[#1976D2] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#1565C0] transition-colors flex items-center gap-2" data-id="element-1016">
                          <EditIcon className="w-4 h-4" data-id="element-1017" />
                          সংশোধন প্রয়োজন
                        </button>
                        <button onClick={() => navigateTo('landRecordDetails')} className="bg-[#E65100] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#BF360C] transition-colors flex items-center gap-2" data-id="element-1018">
                          <FileTextIcon className="w-4 h-4" data-id="element-1019" />
                          সিএলও দেখুন
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div> : <div className="border border-[#D1E7DD] rounded-lg overflow-hidden flex flex-col md:flex-row h-[400px]" data-id="element-1020">
                {/* Columns Header (Visible on Desktop) */}
                <div className="hidden md:flex w-full bg-[#E8F5E9] border-b border-[#D1E7DD] font-bold text-gray-800 text-sm absolute z-10" data-id="element-1021">
                  <div className="w-[15%] p-3 border-r border-[#D1E7DD]" data-id="element-1022">
                    বিভাগ
                  </div>
                  <div className="w-[15%] p-3 border-r border-[#D1E7DD]" data-id="element-1023">
                    জেলা
                  </div>
                  <div className="w-[15%] p-3 border-r border-[#D1E7DD]" data-id="element-1024">
                    উপজেলা/থানা
                  </div>
                  <div className="w-[15%] p-3 border-r border-[#D1E7DD]" data-id="element-1025">
                    খতিয়ানের ধরন
                  </div>
                  <div className="w-[20%] p-3 border-r border-[#D1E7DD]" data-id="element-1026">
                    মৌজা
                  </div>
                  <div className="w-[20%] p-3" data-id="element-1027">খতিয়ানের তালিকা</div>
                </div>

                {/* Table Body */}
                <div className="flex w-full h-full pt-11 relative" data-id="element-1028">
                  {/* Division Column */}
                  <div className="w-[15%] border-r border-[#D1E7DD] overflow-y-auto bg-white" data-id="element-1029">
                    <div className="bg-[#E8F5E9] p-3 border-b border-[#D1E7DD] font-bold text-gray-800 text-sm md:hidden" data-id="element-1030">
                      বিভাগ
                    </div>
                    {Object.keys(cascadeData).map(div => <div key={div} onClick={() => handleDivisionClick(div)} className={`p-3 border-b border-gray-100 text-sm cursor-pointer transition-colors ${selectedDivision === div ? 'bg-green-50 text-[#006A4E] font-bold border-l-4 border-l-[#006A4E]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1031">
                        {div}
                      </div>)}
                  </div>

                  {/* District Column */}
                  <div className="w-[15%] border-r border-[#D1E7DD] overflow-y-auto bg-white" data-id="element-1032">
                    <div className="bg-[#E8F5E9] p-3 border-b border-[#D1E7DD] font-bold text-gray-800 text-sm md:hidden" data-id="element-1033">
                      জেলা
                    </div>
                    {selectedDivision && Object.keys(cascadeData[selectedDivision]).map(dist => <div key={dist} onClick={() => handleDistrictClick(dist)} className={`p-3 border-b border-gray-100 text-sm cursor-pointer transition-colors ${selectedDistrict === dist ? 'bg-green-50 text-[#006A4E] font-bold border-l-4 border-l-[#006A4E]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1034">
                          {dist}
                        </div>)}
                  </div>

                  {/* Upazila Column */}
                  <div className="w-[15%] border-r border-[#D1E7DD] overflow-y-auto bg-white" data-id="element-1035">
                    <div className="bg-[#E8F5E9] p-3 border-b border-[#D1E7DD] font-bold text-gray-800 text-sm md:hidden" data-id="element-1036">
                      উপজেলা/থানা
                    </div>
                    {selectedDivision && selectedDistrict && Object.keys(cascadeData[selectedDivision][selectedDistrict]).map(upa => <div key={upa} onClick={() => handleUpazilaClick(upa)} className={`p-3 border-b border-gray-100 text-sm cursor-pointer transition-colors ${selectedUpazila === upa ? 'bg-green-50 text-[#006A4E] font-bold border-l-4 border-l-[#006A4E]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1037">
                          {upa}
                        </div>)}
                  </div>

                  {/* Khatian Type Column */}
                  <div className="w-[15%] border-r border-[#D1E7DD] overflow-y-auto bg-white" data-id="element-1038">
                    <div className="bg-[#E8F5E9] p-3 border-b border-[#D1E7DD] font-bold text-gray-800 text-sm md:hidden" data-id="element-1039">
                      খতিয়ানের ধরন
                    </div>
                    {selectedUpazila && khatianTypes.map(type => <div key={type} onClick={() => handleKhatianTypeClick(type)} className={`p-3 border-b border-gray-100 text-sm cursor-pointer transition-colors ${selectedKhatianType === type ? 'bg-green-50 text-[#006A4E] font-bold border-l-4 border-l-[#006A4E]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1040">
                          {type}
                        </div>)}
                  </div>

                  {/* Mouza Column */}
                  <div className="w-[20%] border-r border-[#D1E7DD] bg-white p-2 flex flex-col" data-id="element-1041">
                    <div className="bg-[#E8F5E9] p-3 border-b border-[#D1E7DD] font-bold text-gray-800 text-sm md:hidden -mx-2 -mt-2 mb-2" data-id="element-1042">
                      মৌজা
                    </div>
                    <div className="relative mb-2 shrink-0" data-id="element-1043">
                      <SearchIcon className="w-4 h-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1044" />
                      <input type="text" placeholder="অনুসন্ধান" className="w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#006A4E]" data-id="element-1045" />
                    </div>
                    <div className="overflow-y-auto flex-1 -mx-2" data-id="element-1046">
                      {selectedDivision && selectedDistrict && selectedUpazila && selectedKhatianType && cascadeData[selectedDivision][selectedDistrict][selectedUpazila].mouzas.map(mouza => <div key={mouza} onClick={() => handleMouzaClick(mouza)} className={`p-3 border-b border-gray-100 text-sm cursor-pointer transition-colors ${selectedMouza === mouza ? 'bg-green-50 text-[#006A4E] font-bold border-l-4 border-l-[#006A4E]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1047">
                            {mouza}
                          </div>)}
                    </div>
                  </div>

                  {/* Khatian List Column */}
                  <div className="w-[20%] bg-white p-2 flex flex-col" data-id="element-1048">
                    <div className="bg-[#E8F5E9] p-3 border-b border-[#D1E7DD] font-bold text-gray-800 text-sm md:hidden -mx-2 -mt-2 mb-2" data-id="element-1049">
                      খতিয়ানের তালিকা
                    </div>
                    <div className="flex space-x-1 mb-2 shrink-0" data-id="element-1050">
                      <div className="relative flex-1" data-id="element-1051">
                        <SearchIcon className="w-4 h-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-1052" />
                        <input type="text" placeholder="খতিয়ান নং" className="w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#006A4E]" data-id="element-1053" />
                      </div>
                      <button className="bg-[#006A4E] text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-[#004D3A]" data-id="element-1054">
                        খুঁজুন
                      </button>
                    </div>
                    <div className="overflow-y-auto flex-1 -mx-2" data-id="element-1055">
                      {selectedMouza && mockKhatians.map(khatian => <div key={khatian.no} onDoubleClick={() => setSelectedKhatian(khatian)} className="p-3 border-b border-gray-100 text-sm cursor-pointer transition-colors text-gray-700 hover:bg-gray-50" data-id="element-1056">
                            <div className="font-medium" data-id="element-1057">{khatian.no}</div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-1058">
                              {khatian.owner}
                            </div>
                          </div>)}
                    </div>
                  </div>
                </div>
              </div>}
          </div>

          {/* Statistics Section */}
          <div data-id="element-1059">
            <h2 className="text-2xl font-bold text-[#006A4E] mb-6 text-center" data-id="element-1060">
              তথ্যচিত্র
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-id="element-1061">
              {/* Stat Card 1 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center shadow-sm hover:shadow-md transition-shadow" data-id="element-1062">
                <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mr-4" data-id="element-1063">
                  <FileTextIcon className="w-7 h-7 text-[#006A4E]" data-id="element-1064" />
                </div>
                <div data-id="element-1065">
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-1066">২,০৩৭</h3>
                  <p className="text-sm text-gray-600 font-medium" data-id="element-1067">
                    আজকের সেবা গ্রহীতা
                  </p>
                </div>
              </div>

              {/* Stat Card 2 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center shadow-sm hover:shadow-md transition-shadow" data-id="element-1068">
                <div className="w-14 h-14 bg-orange-50 rounded-full flex items-center justify-center mr-4" data-id="element-1069">
                  <GlobeIcon className="w-7 h-7 text-orange-500" data-id="element-1070" />
                </div>
                <div data-id="element-1071">
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-1072">
                    ১,৮৭,৯৫,৯৯৬
                  </h3>
                  <p className="text-sm text-gray-600 font-medium" data-id="element-1073">
                    মোট সেবা গ্রহীতা
                  </p>
                </div>
              </div>

              {/* Stat Card 3 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center shadow-sm hover:shadow-md transition-shadow" data-id="element-1074">
                <div className="w-14 h-14 bg-pink-50 rounded-full flex items-center justify-center mr-4" data-id="element-1075">
                  <FileIcon className="w-7 h-7 text-pink-500" data-id="element-1076" />
                </div>
                <div data-id="element-1077">
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-1078">
                    ৫১,৮৬,৮৪৬
                  </h3>
                  <p className="text-sm text-gray-600 font-medium" data-id="element-1079">
                    মোট ভিজিটর
                  </p>
                </div>
              </div>

              {/* Stat Card 4 */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center shadow-sm hover:shadow-md transition-shadow" data-id="element-1080">
                <div className="w-14 h-14 bg-indigo-50 rounded-full flex items-center justify-center mr-4" data-id="element-1081">
                  <ActivityIcon className="w-7 h-7 text-indigo-500" data-id="element-1082" />
                </div>
                <div data-id="element-1083">
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-1084">
                    ৫,৮৪,১৮,৪৮৪
                  </h3>
                  <p className="text-sm text-gray-600 font-medium" data-id="element-1085">মোট ভিজিট</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 font-medium" data-id="element-1086">
              সর্বশেষ হালনাগাদ: ১২-০৩-২০২৬ ১১:৩২
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#E8F5E9] pt-12 pb-6 border-t-4 border-[#006A4E]" data-id="element-1087">
        <div className="max-w-[1400px] mx-auto px-4" data-id="element-1088">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-1089">
            {/* Column 1: Important Links */}
            <div data-id="element-1090">
              <h3 className="text-lg font-bold text-[#006A4E] mb-4" data-id="element-1091">
                গুরুত্বপূর্ণ লিঙ্ক
              </h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700" data-id="element-1092">
                <a href="#" className="flex items-center hover:text-[#006A4E]" data-id="element-1093">
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-[#006A4E]" data-id="element-1094" />{' '}
                  বাংলাদেশ জাতীয় তথ্য বাতায়ন
                </a>
                <a href="#" className="flex items-center hover:text-[#006A4E]" data-id="element-1095">
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-[#006A4E]" data-id="element-1096" />{' '}
                  সাপোর্ট টিকেট
                </a>
                <a href="#" className="flex items-center hover:text-[#006A4E]" data-id="element-1097">
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-[#006A4E]" data-id="element-1098" />{' '}
                  ভূমি মন্ত্রণালয়
                </a>
                <a href="#" className="flex items-center hover:text-[#006A4E]" data-id="element-1099">
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-[#006A4E]" data-id="element-1100" />{' '}
                  গোপনীয়তা নীতি
                </a>
                <a href="#" className="flex items-center hover:text-[#006A4E]" data-id="element-1101">
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-[#006A4E]" data-id="element-1102" />{' '}
                  তথ্য অধিদপ্তর (পিআইডি)
                </a>
                <a href="#" className="flex items-center hover:text-[#006A4E]" data-id="element-1103">
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-[#006A4E]" data-id="element-1104" />{' '}
                  সাধারণ জিজ্ঞাসা
                </a>
                <a href="#" className="flex items-center hover:text-[#006A4E]" data-id="element-1105">
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-[#006A4E]" data-id="element-1106" />{' '}
                  অভিযোগ প্রতিকার ব্যবস্থা
                </a>
                <a href="#" className="flex items-center hover:text-[#006A4E]" data-id="element-1107">
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-[#006A4E]" data-id="element-1108" />{' '}
                  যোগাযোগ
                </a>
              </div>
            </div>

            {/* Column 2: Planning & Implementation */}
            <div data-id="element-1109">
              <h3 className="text-lg font-bold text-[#006A4E] mb-4" data-id="element-1110">
                পরিকল্পনা ও বাস্তবায়নে
              </h3>
              <div className="flex items-center space-x-4" data-id="element-1111">
                <img src="https://cdn.magicpatterns.com/uploads/64WUNBj7L71rcFyvALcTYv/lmap_logo.png" alt="LMAP Logo" className="h-16 object-contain" data-id="element-1112" />
              </div>
            </div>

            {/* Column 3: App Store */}
            <div className="flex items-center lg:justify-center" data-id="element-1113">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/2560px-Google_Play_Store_badge_EN.svg.png" alt="Get it on Google Play" className="h-12 object-contain cursor-pointer" data-id="element-1114" />
            </div>

            {/* Column 4: Social Media */}
            <div data-id="element-1115">
              <h3 className="text-lg font-bold text-[#006A4E] mb-4 lg:text-right" data-id="element-1116">
                সামাজিক যোগাযোগ
              </h3>
              <div className="flex items-center space-x-3 lg:justify-end" data-id="element-1117">
                <a href="#" className="w-8 h-8 bg-blue-600 text-white rounded flex items-center justify-center hover:bg-blue-700 transition-colors" data-id="element-1118">
                  <FacebookIcon className="w-4 h-4" data-id="element-1119" />
                </a>
                <a href="#" className="w-8 h-8 bg-black text-white rounded flex items-center justify-center hover:bg-gray-800 transition-colors" data-id="element-1120">
                  <TwitterIcon className="w-4 h-4" data-id="element-1121" />
                </a>
                <a href="#" className="w-8 h-8 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 text-white rounded flex items-center justify-center hover:opacity-90 transition-opacity" data-id="element-1122">
                  <InstagramIcon className="w-4 h-4" data-id="element-1123" />
                </a>
                <a href="#" className="w-8 h-8 bg-red-600 text-white rounded flex items-center justify-center hover:bg-red-700 transition-colors" data-id="element-1124">
                  <YoutubeIcon className="w-4 h-4" data-id="element-1125" />
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row justify-between items-center pt-6 border-t border-gray-300 text-sm text-gray-600" data-id="element-1126">
            <p data-id="element-1127">©২০২৬ ভূমি ব্যবস্থাপনা অটোমেশন প্রকল্প</p>
            <div className="flex items-center mt-4 md:mt-0" data-id="element-1128">
              <span className="mr-2 font-bold" data-id="element-1129">কারিগরি সহায়তায়:</span>
              <img src="https://cdn.magicpatterns.com/uploads/vYSGi6LSWZxGWV26FhjYcC/softbd.png" alt="Soft BD Ltd." className="h-8 object-contain ml-2" data-id="element-1130" />
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Chat Button */}
      <button className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-700 transition-colors z-50" data-id="element-1131">
        <MessageCircleIcon className="w-6 h-6" data-id="element-1132" />
      </button>

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `
    }} data-id="element-1133" />
    </div>;
}